import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
    Container, Row, Col, Card, Table, Badge,
    Alert, Spinner, Button, Image
} from 'react-bootstrap';
import {
    Person, Envelope, Telephone, GeoAlt,
    Wallet2, ArrowRepeat, CheckCircle, XCircle,
    Calendar, ArrowLeft, ArrowRight
} from 'react-bootstrap-icons';

import './UserContributions.css';
import { AuthContext } from '../../contexts/AuthContext';

const UserContributions = () => {
    const { user: authUser, token } = useContext(AuthContext);

    // State management
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [usingCachedData, setUsingCachedData] = useState(false);
    const [error, setError] = useState(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total_items: 0,
        total_pages: 1,
        has_next: false,
        has_previous: false
    });

    // Alert state
    const [alert, setAlert] = useState({ show: false, variant: '', message: '' });

    // Base API URL
    const API_BASE_URL = 'https://aceswafund.org/welfare/welfare-system';

    // Local storage keys
    const STORAGE_KEYS = {
        USER_DATA: (userId) => `user_contributions_${userId}`,
        CONTRIBUTIONS: (userId, page) => `contributions_${userId}_page_${page}`,
        PAGINATION: (userId) => `contributions_pagination_${userId}`,
        LAST_UPDATED: (userId) => `contributions_last_updated_${userId}`
    };

    // Save to local storage - matching DependentsManager pattern
    const saveToLocalStorage = useCallback((userId, data) => {
        try {
            // Save user data
            localStorage.setItem(STORAGE_KEYS.USER_DATA(userId), JSON.stringify({
                user: data.user,
                timestamp: new Date().toISOString()
            }));

            // Save contributions for current page
            localStorage.setItem(
                STORAGE_KEYS.CONTRIBUTIONS(userId, data.pagination.current_page),
                JSON.stringify({
                    contributions: data.contributions,
                    page: data.pagination.current_page,
                    timestamp: new Date().toISOString()
                })
            );

            // Save pagination info
            localStorage.setItem(
                STORAGE_KEYS.PAGINATION(userId),
                JSON.stringify({
                    pagination: data.pagination,
                    timestamp: new Date().toISOString()
                })
            );

            console.log('✅ Data saved to local storage');
        } catch (error) {
            console.error('❌ Error saving to local storage:', error);
        }
    }, []);

    // Load from local storage - matching DependentsManager pattern
    const loadFromLocalStorage = useCallback((userId, page) => {
        try {
            const storedUserData = localStorage.getItem(STORAGE_KEYS.USER_DATA(userId));
            const storedContributions = localStorage.getItem(STORAGE_KEYS.CONTRIBUTIONS(userId, page));
            const storedPagination = localStorage.getItem(STORAGE_KEYS.PAGINATION(userId));

            if (storedUserData && storedContributions && storedPagination) {
                const userDataParsed = JSON.parse(storedUserData);
                const contributionsParsed = JSON.parse(storedContributions);
                const paginationParsed = JSON.parse(storedPagination);

                console.log('📦 Loaded from cache');

                return {
                    user: userDataParsed.user,
                    contributions: contributionsParsed.contributions,
                    pagination: paginationParsed.pagination
                };
            }
        } catch (error) {
            console.error('❌ Error loading from local storage:', error);
        }
        return null;
    }, []);

    // Clear local storage
    const clearLocalStorage = useCallback((userId) => {
        try {
            localStorage.removeItem(STORAGE_KEYS.USER_DATA(userId));
            localStorage.removeItem(STORAGE_KEYS.PAGINATION(userId));
            localStorage.removeItem(STORAGE_KEYS.LAST_UPDATED(userId));
            // Clear all pages
            for (let i = 1; i <= 10; i++) {
                localStorage.removeItem(STORAGE_KEYS.CONTRIBUTIONS(userId, i));
            }
            console.log('🗑️ Local storage cleared');
        } catch (error) {
            console.error('❌ Error clearing local storage:', error);
        }
    }, []);

    // Main function to fetch from server - matching DependentsManager exactly
    const fetchFromServer = useCallback(async (page = 1, showRefreshSpinner = false) => {
        if (!authUser?.id || !token) return null;

        if (showRefreshSpinner) {
            setRefreshing(true);
        }

        try {
            const url = `${API_BASE_URL}/get-user-details?user_id=${authUser.id}&page=${page}&limit=10`;
            console.log('🌐 Fetching from server:', url);

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('📡 Server response:', data);

            if (data.status) {
                console.log('✅ Received', data.contributions?.length || 0, 'contributions from server');

                // Update state
                setUserData(data);
                setPagination(data.pagination);

                // Save to local storage
                saveToLocalStorage(authUser.id, data);

                // If we were using cached data, turn that off
                setUsingCachedData(false);
                setError(null);

                return data;
            } else {
                console.log('⚠️ No data or invalid response from server');
                return null;
            }
        } catch (error) {
            console.error('❌ Server fetch error:', error);
            throw error;
        } finally {
            if (showRefreshSpinner) {
                setRefreshing(false);
            }
        }
    }, [authUser?.id, token, API_BASE_URL, saveToLocalStorage]);

    // Load data (with cache fallback) - matching DependentsManager pattern
    const loadData = useCallback(async (page = 1, forceRefresh = false) => {
        setLoading(true);

        try {
            // Try to fetch from server first if forcing refresh
            if (forceRefresh) {
                console.log('🔄 Force refreshing from server...');
                await fetchFromServer(page, true);
                setLoading(false);
                return;
            }

            // Try server first
            try {
                await fetchFromServer(page);
                setLoading(false);
                return;
            } catch (serverError) {
                console.log('⚠️ Server failed, trying cache...');

                // If server fails, try cache
                const cachedData = loadFromLocalStorage(authUser.id, page);
                if (cachedData) {
                    console.log('📦 Using cached data as fallback');
                    setUserData(cachedData);
                    setPagination(cachedData.pagination);
                    setUsingCachedData(true);
                    showAlert('info', 'Using cached data. Server connection failed.');
                } else {
                    // No cache either
                    setUserData(null);
                    setError('Unable to load data. Please try again.');
                    showAlert('warning', 'Unable to load data. Please try again.');
                }
            }
        } catch (error) {
            console.error('❌ Load error:', error);
            showAlert('danger', 'Error loading contributions');
        } finally {
            setLoading(false);
        }
    }, [authUser?.id, fetchFromServer, loadFromLocalStorage]);

    // Manual refresh function
    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            await fetchFromServer(currentPage, true);
            showAlert('success', 'Data refreshed successfully');
        } catch (error) {
            showAlert('danger', 'Failed to refresh data');
        } finally {
            setRefreshing(false);
        }
    };

    // Handle page change
    const handlePageChange = async (newPage) => {
        if (newPage === currentPage) return;

        setCurrentPage(newPage);
        await loadData(newPage);
    };

    // Initial load
    useEffect(() => {
        if (authUser?.id && token) {
            loadData(1);
        } else {
            setLoading(false);
        }
    }, [authUser?.id, token]);

    // Show alert helper
    const showAlert = (variant, message) => {
        setAlert({ show: true, variant, message });
        setTimeout(() => setAlert({ show: false, variant: '', message: '' }), 5000);
    };

    // Format date
    const formatDate = (dateString) => {
        const options = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    // Get status badge variant
    const getStatusBadgeVariant = (status) => {
        return status === 'APPROVED' ? 'success' : 'danger';
    };

    // Calculate statistics
    const getStats = () => {
        if (!userData?.contributions) {
            return { approved: 0, rejected: 0, total: 0 };
        }

        const approved = userData.contributions.filter(c => c.status === 'APPROVED').length;
        const rejected = userData.contributions.filter(c => c.status === 'REJECTED').length;

        return {
            approved,
            rejected,
            total: approved + rejected
        };
    };

    const stats = getStats();

    // Loading state
    if (loading && !userData) {
        return (
            <Container className="py-5">
                <div className="text-center">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Loading contributions...</p>
                </div>
            </Container>
        );
    }

    // Not logged in
    if (!authUser || !token) {
        return (
            <Container className="py-4">
                <Alert variant="warning">
                    Please log in to view your contributions.
                </Alert>
            </Container>
        );
    }

    return (
        <Container className="py-4">
            {/* Alert Messages */}
            {alert.show && (
                <Alert
                    variant={alert.variant}
                    onClose={() => setAlert({ show: false })}
                    dismissible
                    className="mb-3"
                >
                    {alert.message}
                </Alert>
            )}

            {/* Error Display */}
            {error && !usingCachedData && (
                <Alert variant="danger" className="mb-3">
                    {error}
                </Alert>
            )}

            {/* Header with Refresh */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3>My Contributions</h3>
                <Button
                    variant="outline-primary"
                    onClick={handleRefresh}
                    disabled={refreshing || loading}
                    className="d-inline-flex align-items-center"
                >
                    <ArrowRepeat className={`me-2 ${refreshing ? 'spin' : ''}`} />
                    {refreshing ? 'Refreshing...' : 'Refresh'}
                </Button>
            </div>

            {/* Offline Mode Indicator */}
            {usingCachedData && !loading && (
                <Alert variant="warning" className="mb-3">
                    <div className="d-flex align-items-center">
                        <span className="me-2">⚠️</span>
                        <small>You're viewing cached data. Click refresh to sync with server.</small>
                    </div>
                </Alert>
            )}

            {/* User Profile Card */}
            {userData && userData.user && (
                <>
                    <Card className="shadow-sm border-0 mb-4" style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white'
                    }}>
                        <Card.Body>
                            <Row className="align-items-center">
                                <Col md={2} className="text-center text-md-start mb-3 mb-md-0">
                                    {/* <Image
                                        src={userData.user.profile_image || 'https://via.placeholder.com/100'}
                                        roundedCircle
                                        width={100}
                                        height={100}
                                        className="border border-3 border-white shadow-sm"
                                        style={{ objectFit: 'cover' }}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = 'https://via.placeholder.com/100';
                                        }}
                                    /> */}
                                </Col>
                                <Col md={5}>
                                    <h4 className="mb-2">{userData.user.full_name}</h4>
                                    <div className="d-flex flex-column gap-1">
                                        <div className="d-flex align-items-center" style={{ color: 'rgba(255,255,255,0.9)' }}>
                                            <Envelope className="me-2" size={16} />
                                            <small>{userData.user.email}</small>
                                        </div>
                                        <div className="d-flex align-items-center" style={{ color: 'rgba(255,255,255,0.9)' }}>
                                            <Telephone className="me-2" size={16} />
                                            <small>{userData.user.phone}</small>
                                        </div>
                                        {userData.user.address && (
                                            <div className="d-flex align-items-center" style={{ color: 'rgba(255,255,255,0.9)' }}>
                                                <GeoAlt className="me-2" size={16} />
                                                <small>{userData.user.address}</small>
                                            </div>
                                        )}
                                    </div>
                                </Col>
                                <Col md={5}>
                                    <Card className="bg-white bg-opacity-25 border-0">
                                        <Card.Body className="text-center">
                                            <Wallet2 size={32} className="mb-2" style={{ color: 'white' }} />
                                            <h5 className="mb-1" style={{ color: 'white' }}>Total Contributions</h5>
                                            <h3 className="mb-0" style={{ color: 'white' }}>
                                                GH₵ {parseFloat(userData.user.total_contribution || 0).toFixed(2)}
                                            </h3>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Stats Cards */}
                    <Row className="mb-4">
                        <Col md={4}>
                            <Card className="shadow-sm border-0 text-center h-100">
                                <Card.Body>
                                    <CheckCircle size={32} className="text-success mb-2" />
                                    <h5>Approved</h5>
                                    <h3 className="text-success">{stats.approved}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="shadow-sm border-0 text-center h-100">
                                <Card.Body>
                                    <XCircle size={32} className="text-danger mb-2" />
                                    <h5>Rejected</h5>
                                    <h3 className="text-danger">{stats.rejected}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="shadow-sm border-0 text-center h-100">
                                <Card.Body>
                                    <Calendar size={32} className="text-primary mb-2" />
                                    <h5>Total</h5>
                                    <h3 className="text-primary">{stats.total}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Contributions Table */}
                    <Card className="shadow-sm border-0">
                        <Card.Header className="bg-white py-3">
                            <h5 className="mb-0">Contribution History</h5>
                        </Card.Header>
                        <Card.Body>
                            {userData.contributions && userData.contributions.length > 0 ? (
                                <>
                                    <div className="table-responsive">
                                        <Table hover className="mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Amount (GH₵)</th>
                                                    <th>Category</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {userData.contributions.map((contribution) => (
                                                    <tr key={contribution.contribution_id}>
                                                        <td>
                                                            <small>{formatDate(contribution.created_at)}</small>
                                                        </td>
                                                        <td>
                                                            <strong>{parseFloat(contribution.amount).toFixed(2)}</strong>
                                                        </td>
                                                        <td>
                                                            <Badge bg="secondary" pill>
                                                                GH₵ {contribution.category}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <Badge
                                                                bg={getStatusBadgeVariant(contribution.status)}
                                                                className="d-inline-flex align-items-center"
                                                                pill
                                                            >
                                                                {contribution.status === 'APPROVED' ? (
                                                                    <CheckCircle className="me-1" size={12} />
                                                                ) : (
                                                                    <XCircle className="me-1" size={12} />
                                                                )}
                                                                {contribution.status}
                                                            </Badge>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>

                                    {/* Pagination */}
                                    {pagination.total_pages > 1 && (
                                        <div className="d-flex justify-content-between align-items-center mt-4">
                                            <small className="text-muted">
                                                Showing {userData.contributions.length} of {pagination.total_items} contributions
                                            </small>
                                            <div className="d-flex gap-2">
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => handlePageChange(currentPage - 1)}
                                                    disabled={currentPage === 1 || refreshing}
                                                    className="d-inline-flex align-items-center"
                                                >
                                                    <ArrowLeft className="me-1" /> Previous
                                                </Button>
                                                <span className="d-flex align-items-center px-3">
                                                    Page {currentPage} of {pagination.total_pages}
                                                </span>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => handlePageChange(currentPage + 1)}
                                                    disabled={currentPage === pagination.total_pages || refreshing}
                                                    className="d-inline-flex align-items-center"
                                                >
                                                    Next <ArrowRight className="ms-1" />
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="text-center py-5">
                                    <Wallet2 size={48} className="text-muted mb-3" />
                                    <h5>No Contributions Yet</h5>
                                    <p className="text-muted">Your contribution history will appear here.</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </>
            )}

            <style jsx>{`
                .spin {
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </Container>
    );
};

export default UserContributions;